<template>
  <div class="upload-content">
    <div v-if="!uploadedImage" class="unuploaded">
      <p class="description">好きな画像を背景にしよう！</p>
      <label class="button upload-button">
        <input
          ref="file"
          class="file-button"
          type="file"
          @change="uploadImage"
        />
        アップロード
      </label>
    </div>
    <div v-else class="uploaded">
      <label class="button remove-button">
        <button type="button" class="remove-button" @click="removeImage">
          削除する
        </button>
      </label>
      <label class="user-upload">
        <input
          ref="file"
          class="file-button"
          type="file"
          @change="uploadImage"
        />
        <img class="user-upload-image" :src="uploadedImage" />
      </label>
    </div>

    <ul v-if="0 < fileErrorMessages.length" class="error-messages">
      <li v-for="(message, index) in fileErrorMessages" :key="index">
        {{ message }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, SetupContext } from "vue";

type Props = {
  uploadedImage: String;
};

export default defineComponent({
  name: "Upload",
  props: {
    uploadedImage: {
      type: String,
      default: null,
    },
  },
  emits: ["upload-image", "remove-image"],
  setup(props: Props, context: SetupContext) {
    const SIZE_LIMIT = 10000000; // 10MB

    let file = null;
    let fileErrorMessages = [];

    async function uploadImage(event) {
      const files = event.target.files || event.dataTransfer.files;
      file = files[0];

      if (checkFile(file)) {
        const image = await getBase64(file);
        context.emit("upload-image", image);
      }
    }

    function removeImage() {
      context.emit("remove-image", null);
      file = null;
    }

    function getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    }

    function checkFile(file) {
      let result = true;
      fileErrorMessages = [];
      // キャンセルしたら処理中断
      if (!file) {
        result = false;
      }
      // jpeg か png 関連ファイル以外は受付けない
      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        fileErrorMessages.push(
          "アップロードできるのは jpeg画像ファイル か png画像ファイルのみです。"
        );
        result = false;
      }
      // 上限サイズより大きければ受付けない
      if (SIZE_LIMIT < file.size) {
        fileErrorMessages.push(
          "アップロードできるファイルサイズは10MBまでです。"
        );
        result = false;
      }
      return result;
    }

    return {
      file,
      fileErrorMessages,
      uploadImage,
      removeImage,
    };
  },
});
</script>

<style scoped>
.upload-content {
  margin: 0 auto;
}

.user-upload {
  cursor: pointer;
  outline: none;
}

.button {
  cursor: pointer;
  outline: none;
  align-items: center;
  border-radius: 2px;
  box-sizing: border-box;
  display: inline-flex;
  font-weight: 600;
  justify-content: center;
  letter-spacing: 0.3px;
  color: #fff;
  height: 4rem;
  padding: 0 1.6rem;
  max-width: 177px;
}

.button.upload-button {
  background-color: #0074fb;
  border: 1px solid #0051b0;
  margin: 0 auto;
}

.button.upload-button:hover {
  background-color: #4c9dfc;
}

.button.upload-button:active {
  background-color: #0051b0;
}

.user-upload-image {
  cursor: pointer;
  outline: none;
  display: block;
}

.user-upload-image:hover {
  opacity: 0.8;
}

.file-button {
  display: none;
}

.unuploaded {
  display: flex;
  flex-flow: column;
}

.unuploaded .description {
  font-size: 2rem;
  margin-bottom: 2rem;
}

.uploaded {
  align-items: center;
  display: flex;
  flex-flow: column;
}

.uploaded img {
  position: relative;
  max-width: 100%;
  max-height: 100%;
}

.button.remove-button {
  border: none;
  color: #ffffff;
  background-color: #ec6d51;
  margin-bottom: 2rem;
}

.button.remove-button:hover {
  text-decoration: underline;
}

.error-messages {
  color: #cf0000;
  list-style: none;
  margin: 0.4rem 0 0 0;
  padding: 0 0.2rem;
  font-size: 1.6rem;
}
</style>
